import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { useIntl, FormattedMessage } from 'react-intl';
import cn from 'classnames';

import { IconButton } from '@bibliocommons/base-buttons';
import { Link, SecondaryLink } from '@bibliocommons/base-links';
import { Print, Share } from '@bibliocommons/base-icons';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';

import FormattedHTMLMessage from 'app/components/shared/FormattedHTMLMessage';
import Heading from 'app/components/shared/Heading';
import Divider from 'app/components/bibs/shared/Divider';
import { catalogBibShape, libraryShape } from '@bibliocommons/bc-prop-types';
import { PROMO_RECOMMENDATION_TYPE } from 'app/constants/AnalyticsGa4Constants';
import { EVENTS_URL, TAXONOMY_DISPLAY_TYPE, GOOGLE_PLACES_IFRAME } from 'app/constants/EventsConstants';
import {
  eventShape,
  eventAudienceShape,
  eventLanguageShape,
  eventProgramShape,
  eventRoomBookingShape,
  eventTypeShape,
  imageShape
} from 'app/shapes/events';
import { selectCurrentUser } from 'app/selectors/AuthSelector';
import { selectLocalization } from 'app/selectors/LocalizationSelector';
import { selectSpecialHoursEntities } from 'app/selectors/EntitiesSelector';
import { selectCardsForSearch } from 'app/selectors/CardsSelector';
import composeGlobalFailureMessaging from 'app/helpers/messaging/composeGlobalFailureMessaging';
import composeGlobalSuccessMessaging from 'app/helpers/messaging/composeGlobalSuccessMessaging';
import { readSessionStorageItem } from 'app/helpers/storage';
import { mapCatalogBibToDisplayBib } from 'app/helpers/displayBib';
import { isInProgress, parseDateTime } from 'app/helpers/events/utils';
import CardsActions from 'app/actions/CardsActions';
import EventsActions from 'app/actions/EventsActions';
import BackToLink from 'app/components/shared/BackToLink';
import EventTitle from 'app/components/events/EventTitle';
import EventDateStamp from 'app/components/events/EventDateStamp';
import EventDateTime from 'app/components/shared/EventDateTime';
import EventLocation from 'app/components/events/EventLocation';
import EventTaxonomies from 'app/components/events/EventTaxonomies';
import LocationAddress from 'app/components/locations/LocationAddress';
import RelatedDisplayBibs from 'app/components/events/RelatedDisplayBibs';
import SeriesEvents from 'app/components/events/SeriesEvents';
import RelatedEvents from 'app/components/events/RelatedEvents';
import PromoCards from 'app/components/shared/PromoCards';
import EventActionsPanel from './EventActionsPanel';

import './EventsDetailsPage.scss';

const ZOOM = 15;
const ALL_REMAINING = 'ALL_REMAINING';
const POST_TYPES = ['bw_list', 'bw_custom_card', 'post', 'bw_or_card', 'bccms_news'];

function formatTime(time) {
  return moment
    .utc(time, 'THH:mm')
    .format('h:mm a')
    .replace(/\s/g, '');
}

export default function EventsDetailsPage({
  currentLibrary,
  event,
  eventSeries,
  audiences,
  program,
  types,
  languages,
  location,
  featuredImage,
  eventRoomBooking,
  registration,
  catalogBibs,
  eventAccommodations,
  currentPageHref,
  eventSettings,
  isMobileApp,
  hasPromosOnContentPages,
  trackAddToCalendarClick,
  trackCatalogTitleClick,
  trackEventLocationNameClick,
  trackEventLinkClick,
  trackViewAllEventsLinkClick,
  trackViewEventsInThisSeriesClick
}) {
  const intl = useIntl();
  const dispatch = useDispatch();
  const currentUser = useSelector(selectCurrentUser);
  const locale = useSelector(selectLocalization).currentLanguage;
  const eventsInSeries = useSelector(state => state.getIn(['events', 'eventsDetails', 'eventsInSeries']));
  const promoCards = useSelector(selectCardsForSearch);
  const specialHoursEntities = useSelector(selectSpecialHoursEntities);

  const componentRef = useRef(null);
  const sessionStorage = readSessionStorageItem('backToLink');
  const isSeriesRegistration = event.getIn(['definition', 'registrationInfo', 'scope']) === ALL_REMAINING;
  const [fetchSeries, setFetchSeries] = useState(event.get('isRecurring'));
  const isPast = moment(event.get('indexEnd')).isBefore(moment());
  const eventStart = event.getIn(['definition', 'start']);
  const eventEnd = event.getIn(['definition', 'end']);
  const isCancelled = event.getIn(['definition', 'isCancelled']);
  const apiKey = eventSettings?.getIn(['googleMapsOptions', 'apiKey']);
  const mapsApiKey = apiKey ?? 'AIzaSyBxJQzKiIr5VZbtytOSoJwitV2WPGGAAKg';

  useEffect(() => {
    if (componentRef.current) {
      const hrElements = componentRef.current.querySelectorAll('hr');
      hrElements.forEach(hr => {
        hr.setAttribute('aria-hidden', 'true');
      });
    }
  }, []);

  useEffect(() => {
    if (fetchSeries) {
      dispatch(
        EventsActions.searchEventsBySeries({
          seriesId: event.get('seriesId'),
          eventId: event?.get('id'),
          limit: 3
        })
      );
    }

    setFetchSeries(!fetchSeries);
    const eventId = event?.get('id');
    const programId = event?.getIn(['definition', 'programId']);
    const audienceId = event?.getIn(['definition', 'audienceIds', 0]);
    const isVirtual = event?.getIn(['definition', 'isVirtual']);
    const locationId = isVirtual ? 'BC_VIRTUAL' : event?.getIn(['definition', 'branchLocationId']);

    if (programId) {
      dispatch(EventsActions.searchEventsByProgram({ programId, eventId, limit: 4 }));
    }
    if (audienceId && locationId) {
      dispatch(
        EventsActions.searchEventsByAudienceAndLocation({
          audienceId,
          locationId,
          eventId,
          limit: 4
        })
      );
    }

    const metadataIds = Object.keys(catalogBibs?.toJS() || {});
    if (hasPromosOnContentPages && metadataIds?.length) {
      dispatch(
        CardsActions.searchCards({
          metadataIds,
          limit: 4,
          postTypes: POST_TYPES,
          recommendationTypes: [PROMO_RECOMMENDATION_TYPE.CONTEXTUAL]
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event, catalogBibs]);

  function handlePrintClick() {
    window?.print();
  }

  function notifyCopyToClipboardSuccess() {
    dispatch({
      type: 'copy-to-clipboard-success',
      messaging: composeGlobalSuccessMessaging('copy-to-clipboard-success', {
        defaultMessage: 'Event URL has been copied to the clipboard',
        id: 'events.notification_copy_to_clipboard_success'
      })
    });
  }

  const notifyCopyToClipboardFailure = () => {
    dispatch({
      type: 'copy-to-clipboard-failure',
      messaging: composeGlobalFailureMessaging('copy-to-clipboard-failure', {
        defaultMessage: 'Could not copy to clipboard',
        id: 'notification_copy_to_clipboard_failure'
      })
    });
  };

  function handleShareClick() {
    const pageUrl = document.location.href;

    if (navigator?.clipboard?.writeText) {
      navigator.clipboard
        .writeText(pageUrl)
        .then(notifyCopyToClipboardSuccess)
        .catch(notifyCopyToClipboardFailure);
    } else {
      const textArea = document.createElement('textarea');

      textArea.value = pageUrl;
      textArea.style.top = '0';
      textArea.style.left = '0';
      textArea.style.position = 'fixed';

      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        const isCopied = document.execCommand('copy');

        if (isCopied) {
          notifyCopyToClipboardSuccess();
        } else {
          notifyCopyToClipboardFailure();
        }
      } catch {
        notifyCopyToClipboardFailure();
      }

      document.body.removeChild(textArea);
    }
  }

  function renderHeaderBar() {
    return (
      <header className="header-bar">
        {sessionStorage?.href !== currentPageHref && <BackToLink />}
        <div className="print-share-buttons">
          <IconButton
            className="print-button"
            dataKey="print-button"
            aria-label="Print this event"
            handleClick={handlePrintClick}
          >
            <Print />
            <ScreenReaderMessage>
              <FormattedMessage defaultMessage="Print this event" id="events.icon_print" />
            </ScreenReaderMessage>
          </IconButton>
          <IconButton
            className="share-button"
            dataKey="share-button"
            aria-label="Share this event"
            handleClick={handleShareClick}
          >
            <Share />
            <ScreenReaderMessage>
              <FormattedMessage defaultMessage="Share this event" id="events.icon_share" />
            </ScreenReaderMessage>
          </IconButton>
        </div>
      </header>
    );
  }

  function renderImage() {
    return (
      featuredImage && (
        <figure className="event-featured-image">
          <img src={featuredImage.get('url')} alt={featuredImage.get('alt')} aria-hidden={!featuredImage.get('alt')} />
          {featuredImage?.get('caption') && (
            <figcaption className="image-caption">{featuredImage?.get('caption')}</figcaption>
          )}
          <EventDateStamp event={event} />
        </figure>
      )
    );
  }

  function renderBanner() {
    if (isPast && isCancelled) {
      return (
        <div className="text past-cancelled">
          <FormattedMessage
            id="events.banner.past_cancelled"
            defaultMessage="This event is in the past and was canceled"
          />
        </div>
      );
    }

    if (isPast) {
      return (
        <div className="text past">
          <FormattedMessage
            id="events.banner.past"
            defaultMessage="This event is in the past and has already taken place"
          />
        </div>
      );
    }

    if (isCancelled) {
      return (
        <div className="text cancelled">
          <FormattedMessage id="events.banner.cancelled" defaultMessage="This event has been canceled" />
        </div>
      );
    }

    if (isInProgress(event)) {
      return (
        <div className="text inprogress">
          <FormattedMessage id="events.banner.inprogress" defaultMessage="This event is currently in progress" />
        </div>
      );
    }

    return null;
  }

  function renderActionsPanel(isMobile = false) {
    if (isPast && isCancelled) return null;

    let registrationModalKey = `event-registration-desktop${event.get('id')}`;
    if (isMobile) {
      registrationModalKey = `event-registration-${event.get('id')}`;
    }

    return (
      <EventActionsPanel
        currentLibrary={currentLibrary}
        currentUser={currentUser}
        event={event}
        eventSeries={eventSeries}
        isPast={isPast}
        registration={registration}
        eventSettings={eventSettings}
        seriesEvents={eventsInSeries}
        registrationModalKey={registrationModalKey}
        trackAddToCalendarClick={trackAddToCalendarClick}
      />
    );
  }

  function renderEventAccommodations() {
    let accommodations;
    let localizationKey = 'accommodations';

    if (event.getIn(['definition', 'accommodations'])) {
      accommodations = event.getIn(['definition', 'accommodations']);
    } else if (event.getIn(['definition', 'branchLocationId'])) {
      accommodations = eventAccommodations?.libraryLocation;
      localizationKey = 'accommodations_library';
    } else if (event.getIn(['definition', 'isVirtual'])) {
      accommodations = eventAccommodations?.onlineLocation;
      localizationKey = 'accommodations_online';
    } else if (event.getIn(['definition', 'nonBranchLocationId'])) {
      accommodations = eventAccommodations?.offsiteLocation;
      localizationKey = 'accommodations_offsite';
    }

    if (!accommodations) return null;

    return (
      <div className="event-accommodations">
        <Heading className="event-section-heading" tagName="h2" size="moderate" variant>
          <FormattedMessage id={localizationKey} defaultMessage="Accommodations" />
        </Heading>
        {/* eslint-disable-next-line react/no-danger */}
        <div className="accommodations" dangerouslySetInnerHTML={{ __html: accommodations }} />
      </div>
    );
  }

  function renderEventDetails() {
    const isRecurring = event.get('isRecurring');
    const eventKey = event.get('key');
    const seriesKeys = eventSeries.get('eventKeys');
    const sessionNumber = isRecurring && eventKey && seriesKeys?.size ? seriesKeys.indexOf(eventKey) + 1 : null;
    const seriesLink = `${EVENTS_URL}?series=${event.get('seriesId')}`;
    const bibIds = event.getIn(['definition', 'bibIds']);
    const displayBibs = Object.values(catalogBibs.toJS() || {}).map(mapCatalogBibToDisplayBib);
    const isVirtual = event.getIn(['definition', 'isVirtual']);

    const seriesLinkMessage = {
      id: 'events.series.in_this_series',
      defaultMessage: 'in this series'
    };
    const seriesLinkText = intl.formatMessage(seriesLinkMessage);

    return (
      <div className="event-details">
        <EventTitle event={event} titleSize="large" titleTag="h1" isLink={false} />
        {isSeriesRegistration && isRecurring && seriesKeys?.size && (
          <div className="series-info">
            <FormattedHTMLMessage id="events.series.session" values={{ sessionNumber, total: seriesKeys?.size }} />
            &nbsp;
            <SecondaryLink
              className="series-link"
              href={seriesLink}
              dataKey="series-link"
              handleClick={() => trackViewEventsInThisSeriesClick({ linkText: seriesLinkText })}
            >
              {seriesLinkText}
            </SecondaryLink>
          </div>
        )}
        <EventDateTime start={eventStart} end={eventEnd} includeYear />
        <EventLocation
          location={location}
          isVirtual={isVirtual}
          handleLocationClick={({ linkText }) => {
            trackEventLocationNameClick({
              linkText,
              locationData: isVirtual
                ? null
                : {
                    location_id: location?.get('id'),
                    location_name: location?.get('name')
                  }
            });
          }}
        />
        {eventRoomBooking?.get('name') && <div className="event-room">{eventRoomBooking.get('name')}</div>}
        <EventTaxonomies
          program={program}
          types={types}
          audiences={audiences}
          languages={languages}
          displayType={TAXONOMY_DISPLAY_TYPE.PILL}
          displayAll
        />
        <div className="actions-panel-mobile">{renderActionsPanel(true)}</div>
        <Divider />
        <div className="description-section">
          <ScreenReaderMessage>
            <h2>
              <FormattedMessage id="event_description" defaultMessage="Event description" />
            </h2>
          </ScreenReaderMessage>
          <div
            className="event-description"
            ref={componentRef}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: event.getIn(['definition', 'description']) }}
          />
        </div>
        {bibIds?.size > 0 && displayBibs && (
          <RelatedDisplayBibs
            displayBibs={displayBibs}
            currentPageHref={currentPageHref}
            headingId="events.related.titles"
            trackCatalogTitleClick={trackCatalogTitleClick}
          />
        )}
        {renderEventAccommodations()}
        {eventsInSeries?.get('count') > 0 && (
          <>
            <Divider />
            <SeriesEvents
              seriesEvents={eventsInSeries}
              location={location}
              seriesId={event.get('seriesId')}
              isSeriesRegistration={isSeriesRegistration}
              trackEventLinkClick={trackEventLinkClick}
              trackViewAllEventsLinkClick={trackViewAllEventsLinkClick}
            />
          </>
        )}
      </div>
    );
  }

  function renderLocationHours() {
    if (isPast) return null;

    const { date: startDate } = parseDateTime(event.getIn(['definition', 'start']));
    const { date: endDate } = parseDateTime(event.getIn(['definition', 'end']));
    const isMultiDay = startDate !== endDate;
    if (isMultiDay) return null;

    // Don't show regular hours if location is closed or has adjusted hours on the event day
    const hasSpecialHoursOnEventDay = location?.get('specialHoursIds')?.some(id => {
      const sh = specialHoursEntities?.get(id);
      return (
        sh &&
        moment(sh.get('startDate')).isSameOrBefore(eventStart, 'day') &&
        moment(sh.get('endDate')).isSameOrAfter(eventStart, 'day')
      );
    });
    if (hasSpecialHoursOnEventDay) return null;

    const eventDay = moment(eventStart).day(); // monday = 1; get the day from the event start date
    const locationOpenTime = location?.getIn(['sortedHours', eventDay - 1, 'hours', '0', 'openTime']);
    const locationCloseTime = location?.getIn(['sortedHours', eventDay - 1, 'hours', '0', 'closeTime']);
    const locationHours = `${formatTime(locationOpenTime)}–${formatTime(locationCloseTime)}`;

    if (!locationOpenTime && !locationCloseTime) return null;

    return (
      <div className="location-hours">
        <FormattedMessage
          id="events.location_open"
          values={{ locationHours }}
          defaultMessage={`Open from ${locationHours} on the day of the event`}
        />
      </div>
    );
  }

  function renderEventLocationDetails() {
    const libraryName = currentLibrary?.get('longName');
    const isVirtual = event.getIn(['definition', 'isVirtual']);
    const address = location?.get('address');
    const mapLocation = location?.getIn(['mapLocation', 'centrePoint']);
    const lat = mapLocation?.get('lat');
    const lng = mapLocation?.get('lng');
    let googleMapsSrc = null;

    const hasValidAddress = address?.get('number') || address?.get('street') || address?.get('city');
    const locationAddress = hasValidAddress
      ? `${address?.get('number')},${address?.get('street')},${address?.get('city')},${address?.get(
          'state'
        )},${address?.get('country')},${address?.get('zip')}`
      : null;

    if (lat && lng) {
      googleMapsSrc = `${GOOGLE_PLACES_IFRAME}?q=${lat},${lng}&zoom=${ZOOM}&key=${mapsApiKey}&language=${locale}`;
    } else if (locationAddress) {
      const encodedAddress = encodeURIComponent(locationAddress);
      googleMapsSrc = `${GOOGLE_PLACES_IFRAME}?q=${encodedAddress}&zoom=${ZOOM}&key=${mapsApiKey}&language=${locale}`;
    }
    const mapTitle = intl.formatMessage(
      { id: 'event.map.title', defaultMessage: `Map showing location of ${libraryName}` },
      { libraryName }
    );

    return (
      <div className="event-location-details">
        <Heading className="event-section-heading" tagName="h2" size="medium" variant>
          <FormattedMessage id="EVENTS.locations.field" defaultMessage="Location" />
        </Heading>
        <div className="location-info">
          <EventLocation
            location={location}
            isVirtual={isVirtual}
            handleLocationClick={({ linkText }) => {
              trackEventLocationNameClick({
                linkText,
                locationData: isVirtual
                  ? null
                  : {
                      location_id: location?.get('id'),
                      location_name: location?.get('name')
                    }
              });
            }}
          />
          {eventRoomBooking?.get('name') && <div className="event-room">{eventRoomBooking.get('name')}</div>}
          {event.getIn(['definition', 'locationDetails']) && (
            <div className="location-details">{event.getIn(['definition', 'locationDetails'])}</div>
          )}
        </div>
        {event.getIn(['definition', 'branchLocationId']) && renderLocationHours()}
        {!event.getIn(['definition', 'isVirtual']) && googleMapsSrc && (
          <div className="location-address">
            <iframe
              className="location-map"
              frameBorder="0"
              allowFullScreen
              title={mapTitle}
              src={googleMapsSrc}
            ></iframe>
            <LocationAddress location={location} />
          </div>
        )}
      </div>
    );
  }

  function renderContactDetails() {
    const name = event.getIn(['definition', 'contact', 'name']);
    const email = event.getIn(['definition', 'contact', 'email', 'globalValue']);
    const phone = event.getIn(['definition', 'contact', 'phone', 'globalValue']);

    if (name || email || phone) {
      return (
        <div className="event-contact">
          <Heading className="event-section-heading" tagName="h2" size="medium" variant>
            <FormattedMessage id="contact" defaultMessage="Contact" />
          </Heading>
          <div className="contact-details">
            {name && <div className="name">{name}</div>}
            {email && (
              <div className="email">
                <Link className="contact-email" href={`mailto:${email}`} dataKey="contact-email">
                  {email}
                </Link>
              </div>
            )}
            {phone && (
              <div className="phone">
                <SecondaryLink className="contact-phone" href={`tel:${phone}`} dataKey="contact-phone">
                  {phone}
                </SecondaryLink>
              </div>
            )}
          </div>
        </div>
      );
    }
    return null;
  }

  const className = cn('cp-events-details-page', { 'cp-events-details-page--mobile-app': isMobileApp });

  return (
    <div className={className}>
      <div className="container">
        {!isMobileApp && renderHeaderBar()}
        <div className="row">
          <div className="col-lg-9 col-xl-9">
            {renderImage()}
            <div className="event-banner">{renderBanner()}</div>
            {renderEventDetails()}
          </div>
          <div className="col-lg-3 col-xl-3 right-column">
            <div className="actions-panel-desktop">{renderActionsPanel(false)}</div>
            {renderEventLocationDetails()}
            {renderContactDetails()}
          </div>

          <div className="col-lg-12 col-xl-12">
            <RelatedEvents
              event={event}
              trackEventLinkClick={trackEventLinkClick}
              trackViewAllEventsLinkClick={trackViewAllEventsLinkClick}
            />
          </div>
        </div>
      </div>
      {promoCards.size > 0 && (
        <section className="discover-content">
          <div className="container">
            <PromoCards promoCards={promoCards} />
          </div>
        </section>
      )}
    </div>
  );
}

EventsDetailsPage.propTypes = {
  currentLibrary: libraryShape.isRequired,
  event: eventShape.isRequired,
  eventSeries: ImmutablePropTypes.map.isRequired,
  audiences: ImmutablePropTypes.listOf(eventAudienceShape).isRequired,
  languages: ImmutablePropTypes.listOf(eventLanguageShape).isRequired,
  types: ImmutablePropTypes.listOf(eventTypeShape).isRequired,
  program: eventProgramShape,
  eventRoomBooking: eventRoomBookingShape,
  featuredImage: imageShape,
  location: ImmutablePropTypes.map,
  registration: ImmutablePropTypes.mapContains({
    registrationId: PropTypes.string,
    eventCount: PropTypes.number,
    interested: PropTypes.bool,
    registered: PropTypes.bool,
    waitlist: PropTypes.bool
  }),
  catalogBibs: catalogBibShape,
  eventAccommodations: PropTypes.shape({
    id: PropTypes.string.isRequired,
    libraryLocation: PropTypes.string,
    offsiteLocation: PropTypes.string,
    onlineLocation: PropTypes.string
  }),
  currentPageHref: PropTypes.string,
  eventSettings: ImmutablePropTypes.mapContains({
    googleMapsOptions: ImmutablePropTypes.mapContains({
      apiKey: PropTypes.string.isRequired
    }),
    eventRegistrationOptions: ImmutablePropTypes.mapContains({
      providerExternal: PropTypes.bool.isRequired,
      providerBiblioEvents: PropTypes.bool.isRequired,
      maxSeats: PropTypes.number,
      emailRequiredForWaitlist: PropTypes.bool.isRequired
    })
  }),
  isMobileApp: PropTypes.bool,
  hasPromosOnContentPages: PropTypes.bool,
  trackAddToCalendarClick: PropTypes.func.isRequired,
  trackCatalogTitleClick: PropTypes.func.isRequired,
  trackEventLocationNameClick: PropTypes.func.isRequired,
  trackEventLinkClick: PropTypes.func.isRequired,
  trackViewAllEventsLinkClick: PropTypes.func.isRequired,
  trackViewEventsInThisSeriesClick: PropTypes.func.isRequired
};
